import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import Box from '@amzn/meridian/box';
import RadioButton from '@amzn/meridian/radio-button';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useAppSelector } from 'src/store/store';
import Grid from 'src/components/shared/grid/grid';
import { setRestrictionListItem } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import {
  validateConsolidatedWeightTaskChildren,
  WeightRestrictionChildTask,
  WeightRestrictionParentTask,
} from 'src/utils/restrictionUtils';
import HoursSelector from './hoursSelector';

/**
 * Display the individual weight range time selectors for a Parent Weight Range type.
 */
const WeightRestrictionChildValueSelectors = (props: { listIndex: number }) => {
  const { listIndex, ...otherProps } = props;
  const dispatch = useDispatch();
  const { restrictionListItems, isRestrictionsPageReadOnly } = useAppSelector((state) => state.newJmrPage);
  const parentTask = restrictionListItems[listIndex] as WeightRestrictionParentTask;

  // Update parent when child is updated
  const onChange = (task: WeightRestrictionChildTask, i: number) => {
    let parentTaskNew = _.cloneDeep(parentTask);
    parentTaskNew.children[i] = task;
    parentTaskNew = validateConsolidatedWeightTaskChildren(parentTaskNew);
    dispatch(setRestrictionListItem({ item: parentTaskNew, index: listIndex }));
  };

  // Setup the child-tasks array
  const childTasks = useMemo(() => {
    return parentTask.children.filter(
      (task) => task.weightRestriction.maxRange <= parentTask.weightRestrictionMaxRange,
    );
  }, [parentTask.children]);

  // If we toggle off showing the child-selectors, reset all the values/errors
  const toggleOffShowChildSelectors = () => {
    const parentTaskNew = _.cloneDeep(parentTask);
    parentTaskNew.showChildSelectors = false;
    parentTaskNew.children
      .filter((task) => task.weightRestriction.maxRange <= parentTask.weightRestrictionMaxRange)
      .forEach((child) => {
        delete child.error;
        delete child.restrictedValue;
      });
    dispatch(setRestrictionListItem({ item: parentTaskNew, index: listIndex }));
  };

  return (
    <Box {...otherProps}>
      {/* Title */}
      <Box spacingInset="none none 200 none" data-testid="new-jmr__weight-restriction__child-weight-ranges-header">
        <Text>
          Time Restrictions{' '}
          <Text tag="span" color="secondary">
            (Optional)
          </Text>
        </Text>
        <Text>
          Do you need to specify the maximum amount of time in hours the associate can <b>{parentTask.label}</b> weights
          less than <b>{parentTask.weightRestrictionMaxRange} lbs</b>?
        </Text>
      </Box>

      {/* Radio Buttons */}
      <Row spacingInset="none none 300 none">
        <RadioButton
          checked={!parentTask.showChildSelectors}
          disabled={isRestrictionsPageReadOnly}
          onChange={toggleOffShowChildSelectors}
          name="show-child-selectors__no"
          value={false}
          size="medium"
        >
          No
        </RadioButton>
        <RadioButton
          checked={parentTask.showChildSelectors}
          disabled={isRestrictionsPageReadOnly}
          onChange={() =>
            dispatch(setRestrictionListItem({ item: { ...parentTask, showChildSelectors: true }, index: listIndex }))
          }
          name="show-child-selectors__yes"
          value={true}
          size="medium"
          data-testid="new-jmr__weight-restriction__child-weight-range-radio-button__yes"
          data-cy={`new-jmr__weight__restriction__child-weight-range-radio-button__yes__${listIndex}`}
        >
          Yes
        </RadioButton>
      </Row>

      {/* Child Selectors */}
      {parentTask.showChildSelectors && (
        <Grid columns={2}>
          {childTasks.map((task, i) => (
            <Box key={i}>
              <Text
                data-cy="new-jmr__weight-restriction__child-weight-range-hours-selector-label"
                data-testid="new-jmr__weight-restriction__child-weight-range-hours-selector-label"
              >
                {task.weightRestriction.header}
              </Text>
              <HoursSelector
                task={task}
                enableFullShift={true}
                onChange={(task) => onChange(task, i)}
                listIndex={listIndex}
                childIndex={i}
              />
            </Box>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default WeightRestrictionChildValueSelectors;
